import React, { useState, useContext, useEffect } from "react";
import { UserContext } from "../context/UserContext"; // Ensure UserContext is correctly imported
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CategoryIcon from "@mui/icons-material/Category";
import ImageIcon from "@mui/icons-material/Image";
import StarIcon from "@mui/icons-material/Star";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import axios from "axios";
import API_BASE_URL from "../../src/config";

const PostService = () => {
  const { user, loading } = useContext(UserContext); // Access user and loading from context
  const [service, setService] = useState({
    service_name: "",
    your_name: "",
    contact_number: "",
    location: "",
    description: "",
    price: "",
    category: "",
    feature_listing: false,
    verification_badge: false,
    image_url: null,
  });
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [contactNumberError, setContactNumberError] = useState("");
  const [error, setError] = useState("");

  const categories = [
    "Home & Living",
    "Business Support",
    "Travel & Logistics",
    "Event & Party Services",
    "Education & Learning",
    "Digital & Tech Services",
    "Special Services",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setService({
      ...service,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSuccessMessage("");
    setErrorMessage("");

    // Check if user is logged in and extract the user ID
    if (!user || !user.id) {
      setErrorMessage("❌ User not logged in.");
      setSubmitted(false);
      return;
    }

 // Validate contact number before submitting
 if (!/^[0-9]{10}$/.test(service.contact_number)) {
  setErrorMessage("❌ Contact number must be exactly 10 digits.");
  setSubmitted(false);
  return;
}

    try {
      // Send service posting with user_id
      const serviceData = { ...service, user_id: user.id }; // Add user_id from context
      console.log("Service Data being sent:", serviceData); // Log service data for debugging

      const response = await axios.post(
        `${API_BASE_URL}/services`,
        serviceData,
        {
          // const response = await axios.post("http://localhost:5000/api/services", serviceData,  {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setSuccessMessage(
        `✅ Service Posted: ${response.data.service_name} by ${response.data.your_name}`
      );

      // Reset form after submission
      setService({
        service_name: "",
        your_name: "",
        contact_number: "",
        location: "",
        description: "",
        price: "",
        category: "",
        feature_listing: false,
        verification_badge: false,
        image_url: null,
      });
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error || "❌ Failed to post service"
      );
      console.error("Error posting service:", error);
    }
    setSubmitted(false);
  };

  // While user data is being fetched, show a loading state
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Typography variant="h6" color="textSecondary" align="center">
          Loading user data... Please wait.
        </Typography>
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h5"
            color="#42326e"
            fontWeight="bold"
            textAlign="center"
            mb={3}
          >
            🛠️ Post a Service
          </Typography>

          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <BuildIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Service Name"
                    variant="outlined"
                    name="service_name"
                    value={service.service_name}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Your Name"
                    variant="outlined"
                    name="your_name"
                    value={service.your_name}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Contact Number"
                    variant="outlined"
                    name="contact_number"
                    value={service.contact_number}
                    onChange={handleChange}
                    required
                    error={!!contactNumberError}
                    helperText={contactNumberError}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOnIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Location"
                    variant="outlined"
                    name="location"
                    value={service.location}
                    onChange={handleChange}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CategoryIcon sx={{ color: "#42326e" }} />
                  <TextField
                    select
                    fullWidth
                    label="Category"
                    variant="outlined"
                    name="category"
                    value={service.category}
                    onChange={handleChange}
                    required
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DescriptionIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Service Description"
                    variant="outlined"
                    name="description"
                    value={service.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    required
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CurrencyRupeeIcon sx={{ color: "#42326e" }} />
                  <TextField
                    fullWidth
                    label="Price (Optional)"
                    variant="outlined"
                    name="price"
                    value={service.price}
                    type="number"
                    onChange={handleChange}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <ImageIcon sx={{ color: "#42326e"}} />
                  <TextField
                    fullWidth
                    label="Image URL"
                    variant="outlined"
                    name="image_url"
                    value={service.image_url}
                    onChange={handleChange}
                    required
                  />
                </Box>

                {/* Image Preview */}
                {service.image_url && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                  >
                    <img
                      src={service.image_url}
                      alt="Service Preview"
                      style={{
                        maxWidth: "100%",
                        height: "150px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="feature_listing"
                      checked={service.feature_listing}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <>
                      <StarIcon color="secondary" /> Feature as Premium Listing
                      (Paid)
                    </>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="verification_badge"
                      checked={service.verification_badge}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <>
                      <VerifiedUserIcon color="success" /> Request Verification
                      Badge
                    </>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    mt: 2,
                    backgroundColor: "#42326e",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "black",
                      color: "white",
                    },
                  }}
                  fullWidth
                  type="submit"
                  disabled={submitted}
                >
                  {submitted ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Post Service"
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PostService;
